import useNeveForm from 'components/forms/NeveForm';
import strings from 'strings/strings.json';
import useUploadRequest from 'hooks/useUploadRequest';
import useModifyRequest from 'hooks/useModifyRequest';
import React from 'react';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import { Activity, ActivityType, BuildMethod } from 'models/Activity';
import BaseDetailsForm from '../shared/DetailsForm';
import { ActivityFormCard } from '../shared/ActivityFormCard';
import { AddImageComponent } from '../../AddImagesComponent';
import TextAreaField from 'components/forms/TextAreaField';
import { createErrorMessage, fieldErrorCodes } from 'shared/error-messages';
import { CreatePresentationSlide, PresentationSlideForm, PresentationSlideImage } from './PresentationSlide';
import { FileDocumentType } from 'models/FileDocument';

type AddPresentationSlideFormProps = {
    returnToModulesList: () => void;
    moduleId: string;
};

export default function AddPresentationSlideForm({
    returnToModulesList,
    moduleId,
}: AddPresentationSlideFormProps): JSX.Element {
    const { refetchCourse, editMode, setCourseState } = React.useContext(BuildCourseContext);
    const [error, setError] = React.useState('');

    const { upload: uploadImage, uploading: uploadingImage } = useUploadRequest();
    const { modifyData: createSlide, loading: creatingSlide } = useModifyRequest<CreatePresentationSlide, boolean>(
        `activities/slide`,
        'POST',
    );

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        setValue,
    } = useNeveForm<PresentationSlideForm>({
        title: '',
        content: '',
        image: null,
    });

    async function saveSlide(formData: PresentationSlideForm) {
        setError('');

        if (!(formData.content?.trim() ?? '') && !formData.image) {
            return setError(strings.presentationSlide.errorRequired);
        }

        let slideImage: PresentationSlideImage | undefined = undefined;

        if (formData.image?.type === 'new') {
            const { errors, value } = await uploadImage(formData.image.file);
            if (errors?.length || !value) {
                setError(strings.presentationSlide.errorCreate);
                return;
            }
            slideImage = {
                type: 'new',
                fileName: value.filename,
                filePath: value.s3Key,
                imageDescription: formData.image.imageDescription,
            };
        } else if (formData.image?.type === 'existing') {
            slideImage = {
                type: 'existing',
                imageId: formData.image.id,
                fileName: formData.image.fileName,
                filePath: formData.image.filePath,
                imageDescription: '',
            };
        }

        if (!editMode) {
            const request: CreatePresentationSlide = {
                title: formData.title,
                moduleId: moduleId,
                content: formData.content?.trim() ?? '',
                image: slideImage,
            };
            const { errors } = await createSlide(request);
            if (errors?.length) {
                setError(strings.presentationSlide.errorCreate);
                return;
            }
            await refetchCourse();
        } else {
            setCourseState((prev) => {
                if (!prev) return;
                const moduleIndex = prev.modules.findIndex((m) => m.id === moduleId);
                if (moduleIndex === -1) return prev;
                const module = prev.modules[moduleIndex];

                const newSlideOrder =
                    module.activities.filter((a) => a.type === ActivityType.PRESENTATION_SLIDE).length + 1;

                const imageDocuments = slideImage
                    ? [
                          {
                              id: slideImage.type === 'existing' ? slideImage.imageId : '',
                              filename: slideImage.fileName,
                              s3Filepath: slideImage.filePath,
                              dateUpdated: new Date(),
                              documentType: FileDocumentType.IMAGE,
                          },
                      ]
                    : [];

                const newSlide: Activity = {
                    id: '',
                    type: ActivityType.PRESENTATION_SLIDE,
                    title: formData.title,
                    description: '',
                    content: formData.content?.trim() ?? '',
                    imageDocuments: imageDocuments,
                    order: newSlideOrder,
                    resources: [],
                    questions: [],
                    statuses: [],
                    meetings: [],
                    dateCreated: new Date(),
                    durationInMins: null,
                    videoDocument: null,
                    audioDocument: null,
                    moduleId: moduleId,
                    buildMethod: BuildMethod.NONE,
                    generateContentStatus: null,
                    taskContents: [],
                };

                return {
                    ...prev,
                    modules: prev.modules.map((m, i) =>
                        i === moduleIndex ? { ...m, activities: [...m.activities, newSlide] } : m,
                    ),
                };
            });
        }

        returnToModulesList();
    }

    const content = watch('content');
    const image = watch('image');
    const contentMaxLength = 2000;

    return (
        <ActivityFormCard
            title={strings.presentationSlide.titleCreate}
            onSubmit={handleSubmit(saveSlide)}
            activityType={ActivityType.PRESENTATION_SLIDE}
            error={error}
            submitting={uploadingImage || creatingSlide}
            pageOne={
                <>
                    <BaseDetailsForm register={register} errors={errors} autoFocus hideShortDescription hideDuration />
                    <TextAreaField
                        maxLength={contentMaxLength}
                        charactersLeft={contentMaxLength - (content ?? '').length}
                        fieldName={strings.taskContentForm.contentLabel}
                        aria={strings.taskContentForm.contentAria}
                        errorMessage={errors.content?.message}
                        min-height={500}
                        {...register('content', {
                            maxLength: {
                                value: contentMaxLength,
                                message: createErrorMessage(
                                    strings.taskContentForm.contentLabel,
                                    fieldErrorCodes.maxLength,
                                ),
                            },
                        })}
                    />
                </>
            }
            pageTwo={
                <AddImageComponent
                    image={image}
                    setImage={(img) => setValue('image', img, { shouldDirty: true })}
                    disableButtons={uploadingImage || creatingSlide}
                />
            }
        />
    );
}
